import React, { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router';
import { FileText, Search, Plus, WorkflowIcon, ChevronRight } from 'lucide-react';
import BottomNav from '../../components/BottomNav.tsx';
import { VehicleModel } from '../../Interfaces/vehicleModle.ts';
import { Building } from '../../Interfaces/building.ts';
import { WorkStation } from '../../Interfaces/workstation.ts';
import { processDocNJMH } from '../../utils/workInstructionVMF.ts';
import { baseurly } from '../../utils/apis.ts';
import { ProcessDoc } from '../../Interfaces/processDocs.ts';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { DesignDoc } from '../../Interfaces/designDocs.ts';
import { X_API_KEY } from '../../utils/apis.ts';
import { baseurl } from '../../utils/apis.ts';
import MainLayout from '../../layouts/MainLayout.tsx';
import { useAuth } from '../../context/authContext.tsx';
import { buildUniqueUploadFile } from '../../utils/uploadFileNaming.ts';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

interface RouteParams {
  vehicleId: string;
}

const DynamicWorkstationPage: React.FC = () => {
  const {employeeI, authReady} = useAuth()
  const { vehicleId } = useParams() as unknown as RouteParams;
  const [refresh, setRefresh] = useState<boolean>(false);
  const navigate = useNavigate();

  // State for vehicle details.
  const [vehicle, setVehicle] = useState<VehicleModel | null>(null);
  const [loadingVehicle, setLoadingVehicle] = useState<boolean>(true);

  // State for buildings.
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loadingBuildings, setLoadingBuildings] = useState<boolean>(true);

  // Error state.
  const [error, setError] = useState<string | null>(null);

  // Processdoc state.
  const [processDoc, setProcessDoc] = useState<ProcessDoc | undefined>(undefined);
  const [showAddProcessDoc, setShowAddProcessDoc] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  // Design Doc
  const [designDoc, setDesignDoc] = useState<DesignDoc | undefined>(undefined);
  const [showAddDesignDoc, setShowAddDesignDoc] = useState<boolean>(false);
  const [designDocFile, setDesignDocFile] = useState<File | null>(null);
  const [submittingDesignDoc, setSubmittingDesignDoc] = useState<boolean>(false);
  const [designDocError, setDesignDocError] = useState<string | null>(null);

  // Build Process Modal State.
  const [showAddProcessModal, setShowAddProcessModal] = useState<boolean>(false);
  const [buildProcessName, setBuildProcessName] = useState<string>('');
  const [buildProcessDescription, setBuildProcessDescription] = useState<string>('');
  const [buildProcessFile, setBuildProcessFile] = useState<File | null>(null);
  const [addingProcess, setAddingProcess] = useState<boolean>(false);
  const [addProcessError, setAddProcessError] = useState<string | null>(null);

  // Work Instruction Modal State.
  const [showAddWorkInstructionModal, setShowAddWorkInstructionModal] = useState<boolean>(false);
  const [wiBuildingId, setWiBuildingId] = useState<string>('');
  const [wiWorkStationId, setWiWorkStationId] = useState<string>('');
  const [wiTitle, setWiTitle] = useState<string>('');
  const [wiDescription, setWiDescription] = useState<string>('');
  const [wiFile, setWiFile] = useState<File | null>(null);
  const [modalWorkStations, setModalWorkStations] = useState<WorkStation[]>([]);
  const [addingWorkInstruction, setAddingWorkInstruction] = useState<boolean>(false);
  const [addWorkInstructionError, setAddWorkInstructionError] = useState<string | null>(null);
  // New states for drawing and video files in the modal.
  const [wiDrawingFile, setWiDrawingFile] = useState<File | null>(null);
  const [wiVideoFile, setWiVideoFile] = useState<File | null>(null);

  // Work instructions list.
  const [workInstructions, setWorkInstructions] = useState<
    {
      id: number;
      buildingId: string;
      workStationId: string;
      title: string;
      description: string;
      file: string | null;
      drawing: string | null;
      video: string | null;
      isSubmitted: boolean;
    }[]
  >([
    {
      id: Date.now(),
      buildingId: "",
      workStationId: "",
      title: "",
      description: "",
      file: null,
      drawing: null,
      video: null,
      isSubmitted: false,
    },
  ]);
  const [vehicleModelId, setVehicleModelId] = useState<number | null>(null);
  const [isModelSubmitted, setIsModelSubmitted] = useState(false);
  const [buildProcess, setBuildProcess] = useState<{
    name: string;
    description: string;
    file: string | null;
    isSubmitted: boolean;
  }>({
    name: "",
    description: "",
    file: null,
    isSubmitted: false,
  });

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate('/login', {replace: true});
      return;
    }
    if (vehicleId) {
      fetch(`${baseurly}/vehicle_model/${vehicleId}`,{
              
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-API-KEY': X_API_KEY,
            },
            credentials: "include",
          })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch vehicle model');
          }
          return res.json();
        })
        .then((data) => {
          console.log(data.process_doc);
          setVehicle(data);
          setLoadingVehicle(false);
          setProcessDoc(data.process_doc);
          setDesignDoc(data.design_doc); // Added to load design doc if it exists
        })
        .catch((err) => {
          console.error('Error fetching vehicle model:', err);
          setError('Unable to load vehicle model');
          setLoadingVehicle(false);
        });

      fetch(`${baseurly}/buildings`,{
              
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': X_API_KEY,
        },
        credentials: "include",
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch buildings');
          }
          return res.json();
        })
        .then((data) => {
          setBuildings(data);
          setLoadingBuildings(false);
        })
        .catch((err) => {
          console.error('Error fetching buildings:', err);
          setError('Unable to load buildings');
          setLoadingBuildings(false);
        });
    }
  }, [authReady, employeeI, navigate, vehicleId, refresh]);

  // Fetch the default work station for a building and navigate.
  const handleBuildingView = (buildingId: number) => {
    fetch(`${baseurly}/buildings/${buildingId}/work_stations`, {
            
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': X_API_KEY,
          },
          credentials: "include",
        })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch work station');
        }
        return res.json();
      })
      .then((workStation: WorkStation) => {
        const workstationId = workStation.id;
        if (vehicle) {
          navigate(`/vehicle/${vehicle.id}/building/${buildingId}`);
        }
      })
      .catch((err) => {
        console.error('Error fetching work station:', err);
        alert('Unable to fetch work station for this building.');
      });
  };

  // Handle adding a new build process.
  const handleAddBuildProcess = async (e: FormEvent) => {
    e.preventDefault();
    if (!buildProcessFile) {
      setAddProcessError('Please select a file.');
      return;
    }
    if (!buildProcessName.trim()) {
      setAddProcessError('Please enter a title.');
      return;
    }
    setAddProcessError(null);
    setAddingProcess(true);
    try {
      const formData = new FormData();
      formData.append('file', buildProcessFile);

      const uploadResponse = await fetch(`${baseurly}/upload/buildingProcess`, {
        method: 'POST',
        headers: {
          'X-API-KEY': X_API_KEY,
        },
        body: formData,
      });
      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }
      const uploadData = await uploadResponse.json();
      const payload = {
        name: buildProcessName,
        description: buildProcessDescription,
        file: uploadData,
        vehicle_model_id: vehicle ? vehicle.id : null,
      };
      const buildProcessResponse = await fetch(`${baseurly}/build_process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                    'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });
      if (!buildProcessResponse.ok) {
        throw new Error('Failed to create build process');
      }
      setBuildProcessName('');
      setBuildProcessDescription('');
      setBuildProcessFile(null);
      setShowAddProcessModal(false);
      alert('Build process added successfully!');
    } catch (err) {
      console.error(err);
      setAddProcessError((err as Error).message);
    } finally {
      setAddingProcess(false);
    }
  };

  // Fetch workstations for the work instruction modal.
  const fetchModalWorkStations = async (buildingId: string) => {
    if (!buildingId) {
      setModalWorkStations([]);
      return;
    }
    try {
      const response = await fetch(`${baseurly}/buildings/${buildingId}/work_stations`,
        {
              
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': X_API_KEY,
          },
          credentials: "include",
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch workstations');
      }
      const data = await response.json();
      setModalWorkStations(data);
    } catch (error) {
      console.error("Error fetching workstations for modal:", error);
    }
  };

  // Handle adding a work instruction via modal.
  const handleAddWorkInstruction = async (e: FormEvent) => {
    e.preventDefault();
    if (!wiBuildingId || !wiWorkStationId || !wiTitle || !wiDescription) {
      setAddWorkInstructionError("Please fill in all required fields.");
      return;
    }
    if (!wiFile && !wiDrawingFile && !wiVideoFile) {
      setAddWorkInstructionError(
        "Please upload at least one file: a work instruction, drawing, or video."
      );
      return;
    }
    setAddWorkInstructionError(null);
    setAddingWorkInstruction(true);
    try {
      let uploadData = null;
      if (wiFile) {
        const formData = new FormData();
        const renamedInstructionFile = buildUniqueUploadFile({
          assetType: 'instruction',
          file: wiFile,
          title: wiTitle,
          vehicleModelId: vehicle?.id,
          workstationId: wiWorkStationId,
        });
        formData.append("file", renamedInstructionFile);
        const uploadResponse = await fetch(`${baseurly}/upload/workInstruction`, {
          method: "POST",
          headers: {
            "X-API-KEY": X_API_KEY,
          },
          body: formData,
        });
        if (!uploadResponse.ok) {
          throw new Error("Work instruction upload failed");
        }
        uploadData = await uploadResponse.json();
      }

      let drawingData = null;
      if (wiDrawingFile) {
        const drawingForm = new FormData();
        const renamedDrawingFile = buildUniqueUploadFile({
          assetType: 'drawing',
          file: wiDrawingFile,
          title: wiTitle,
          vehicleModelId: vehicle?.id,
          workstationId: wiWorkStationId,
        });
        drawingForm.append("file", renamedDrawingFile);
        const drawingResponse = await fetch(`${baseurly}/upload/drawing`, {
          method: "POST",
          headers: {
            "X-API-KEY": X_API_KEY,
          },
          body: drawingForm,
        });
        if (!drawingResponse.ok) {
          throw new Error("Drawing upload failed");
        }
        drawingData = await drawingResponse.json();
      }

      // Upload video if provided.
      let videoData = null;
      if (wiVideoFile) {
        const videoForm = new FormData();
        const renamedVideoFile = buildUniqueUploadFile({
          assetType: 'video',
          file: wiVideoFile,
          title: wiTitle,
          vehicleModelId: vehicle?.id,
          workstationId: wiWorkStationId,
        });
        videoForm.append("file", renamedVideoFile);
        const videoResponse = await fetch(`${baseurly}/upload/video`, {
          method: "POST",
          headers: {
            "X-API-KEY": X_API_KEY,
          },
          body: videoForm,
        });
        if (!videoResponse.ok) {
          throw new Error("Video upload failed");
        }
        videoData = await videoResponse.json();
      }

      const payload = {
        author: employeeI ? `${employeeI.fname} ${employeeI.lname}` : "Unknown",
        author_email: employeeI?.email || "unknown@kiiramotors.com",
        title: wiTitle,
        description: wiDescription,
        vehicle_model_id: vehicle ? vehicle.id : null,
        work_station_id: Number(wiWorkStationId),
        ...(uploadData ? { file: uploadData } : {}),
        ...(drawingData ? { drawing: drawingData } : {}),
        ...(videoData ? { video: videoData } : {}),
      };

      const response = await fetch(`${baseurly}/work_instruction`, {
        method: "POST",
        headers: { "Content-Type": "application/json",
                  'X-API-KEY': X_API_KEY,
         },

        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        if (!wiFile) {
          throw new Error(
            "This server still requires a work instruction document. Please upload the document PDF and try again."
          );
        }
        throw new Error("Failed to submit work instruction");
      }
      alert("Work instruction added successfully!");
      // Reset modal fields.
      setWiBuildingId("");
      setWiWorkStationId("");
      setWiTitle("");
      setWiDescription("");
      setWiFile(null);
      setWiDrawingFile(null);
      setWiVideoFile(null);
      setModalWorkStations([]);
      setShowAddWorkInstructionModal(false);
    } catch (error) {
      console.error("Error adding work instruction:", error);
      setAddWorkInstructionError((error as Error).message);
    } finally {
      setAddingWorkInstruction(false);
    }
  };

  // For list items: handle drawing upload.
  const handleDrawingUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      const renamedDrawingFile = buildUniqueUploadFile({
        assetType: 'drawing',
        file,
        title: workInstructions[index]?.title,
        vehicleModelId: vehicle?.id,
        workstationId: workInstructions[index]?.workStationId,
      });
      formData.append("file", renamedDrawingFile);
      const response = await fetch(`${baseurly}/upload/drawing`, {
        method: "POST",
        headers: {
          "X-API-KEY": X_API_KEY,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Drawing upload failed");
      }
      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) =>
          i === index ? { ...wi, drawing: JSON.parse(filePath) } : wi
        )
      );
    } catch (error) {
      console.error("Error uploading drawing:", error);
    }
  };

  // For list items: handle video upload.
  const handleVideoUpload = async (file: File, index: number) => {
    try {
      const formData = new FormData();
      const renamedVideoFile = buildUniqueUploadFile({
        assetType: 'video',
        file,
        title: workInstructions[index]?.title,
        vehicleModelId: vehicle?.id,
        workstationId: workInstructions[index]?.workStationId,
      });
      formData.append("file", renamedVideoFile);
      const response = await fetch(`${baseurly}/upload/video`, {
        method: "POST",
        headers: {
          "X-API-KEY": X_API_KEY,
        },
        credentials: "include",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Video upload failed");
      }
      const filePath = await response.text();
      setWorkInstructions((prev) =>
        prev.map((wi, i) =>
          i === index ? { ...wi, video: JSON.parse(filePath) } : wi
        )
      );
    } catch (error) {
      console.error("Error uploading video:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleDesignDocFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setDesignDocFile(e.target.files[0]);
    }
  };

  const handleSubmitProcessDoc = async () => {
    if (!file || !vehicleId) {
      alert('Please upload a file.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const uploadResponse = await fetch(`${baseurly}/upload/processDoc`, {
        method: 'POST',
        headers: {
          'X-API-KEY': X_API_KEY,
        },
        credentials: 'include',
        body: formData,
      });
      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }
      const filePath = await uploadResponse.text();
      const payload = {
        vehicle_model_id: parseInt(vehicleId),
        file: JSON.parse(filePath),
      };
      const submitResponse = await fetch(`${baseurly}/process_docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                  'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });
      if (!submitResponse.ok) {
        throw new Error('Failed to submit process document');
      }
      let dataL = await submitResponse.json();
      alert('Process document submitted successfully!');
      setShowAddProcessDoc(false);
      setProcessDoc(dataL);
      setRefresh(!refresh);
      setFile(null);
    } catch (error) {
      console.error('Error submitting process document:', error);
      alert('Failed to submit process document');
    }
  };

  const handleSubmitDesignDoc = async () => {
    if (!designDocFile || !vehicleId) {
      setDesignDocError('Please upload a file for Technical Specifications.');
      return;
    }
    
    setDesignDocError(null);
    setSubmittingDesignDoc(true);
    
    try {
      // First upload the file
      const formData = new FormData();
      formData.append('file', designDocFile);

      const uploadResponse = await fetch(`${baseurly}/upload/designDoc`, {
        method: 'POST',
        headers: {
          'X-API-KEY': X_API_KEY,
        },
        credentials: 'include',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Technical Specifications document upload failed');
      }

      const uploadData = await uploadResponse.json();
      const payload = {
        vehicle_model_id: parseInt(vehicleId),
        file: uploadData,
      };

      const submitResponse = await fetch(`${baseurly}/design_docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                  'X-API-KEY': X_API_KEY,
         },
        body: JSON.stringify(payload),
      });

      if (!submitResponse.ok) {
        throw new Error('Failed to submit Technical Specifications document');
      }

      const responseData = await submitResponse.json();
      // Update state and UI
      setDesignDoc(responseData);
      setShowAddDesignDoc(false);
      setDesignDocFile(null);
      setRefresh(!refresh);
      
      alert('Technical Specifications document submitted successfully!');
    } catch (error) {
      console.error('Error submitting Technical Specifications document:', error);
      setDesignDocError((error as Error).message || 'Failed to submit Technical Specifications document');
    } finally {
      setSubmittingDesignDoc(false);
    }
  };

  function handleAddProcessDocClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    setShowAddProcessDoc(true);
  }

  function handleAddDesignDocClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    setShowAddDesignDoc(true);
  }

  return (
    <MainLayout>
    <div className="min-h-screen bg-blue-900 pb-20">
      {/* Header */}
      

      {/* Page Title */}
      <div className="p-6">
        <h1 className="text-white text-lg font-medium">
          {loadingVehicle ? 'Loading...' : vehicle ? vehicle.model_name : 'Unknown Vehicle'} Details
        </h1>
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for instructions..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Vehicle Details Card */}
      <div className="bg-white ring-2 ring-white-500 p-4 rounded-t-3xl">
        <div className="bg-white border rounded-3xl p-8 mx-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-blue-900 text-xl font-bold mb-2">Vehicle Details</h2>
              <p className="text-gray-600">Model: {vehicle ? vehicle.model_name : 'Not available'}</p>
              <p className="text-gray-600">Type: {vehicle ? vehicle.vehicle_type : 'N/A'}</p>
              <div className="flex mt-4 gap-2">
                <button
                  onClick={() => navigate(`/vehicle/${vehicleId}/build-process`)}
                  className="bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
                >
                  <FileText size={16} />
                  View Build Process
                </button>
                <button
                  onClick={() => setShowAddProcessModal(true)}
                  className="bg-blue-800 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Build Process
                </button>
                <button
                  onClick={() => setShowAddWorkInstructionModal(true)}
                  className="bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center gap-2"
                >
                  <WorkflowIcon size={16} />
                  Add Work Instruction
                </button>
              </div>
              <div className='flex gap-2'>
                {processDoc && processDoc.id > 0 && (
                  <button
                    onClick={() => navigate(`/process-document/${processDoc?.id}`)}
                    className="mt-4 bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
                  >
                    <FileText size={16} />
                    View Process Document
                  </button>
                )}
                <button
                  onClick={handleAddProcessDocClick}
                  className="mt-4 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Process Document
                </button>
              </div>
              <div className="flex mt-4 gap-2">
                {designDoc && designDoc.id > 0 && (
                  <button
                    onClick={() => navigate(`/designSpec-document/${designDoc?.id}`)}
                    className="bg-blue-900 text-white px-4 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2"
                  >
                    <FileText size={16} />
                    View Technical Specifications
                  </button>
                )}
                <button
                  onClick={handleAddDesignDocClick}
                  className="bg-blue-800 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={16} />
                  Add Technical Specifications
                </button>
              </div>
            </div>
            <div>
              <img
                src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
                alt={vehicle ? vehicle.model_name : 'Placeholder'}
                className="w-29 h-24 object-contain"
              />
            </div>
          </div>
          {/* Add Process Document Modal */}
          {showAddProcessDoc && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
              <div className="bg-white p-6 rounded-lg w-96">
                <h2 className="text-blue-900 text-xl font-bold mb-4">Add Process Document</h2>
                <input
                  type="file"
                  onChange={handleFileChange}
                  className="w-full p-2 border rounded mb-4"
                  accept=".pdf,.doc,.docx"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setShowAddProcessDoc(false);
                      setFile(null);
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitProcessDoc}
                    className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
                    disabled={!file}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Design Document Modal */}
        {showAddDesignDoc && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h2 className="text-blue-900 text-xl font-bold mb-4">Add Technical Specifications Document</h2>
              {designDocError && <p className="text-red-500 mb-2">{designDocError}</p>}
              <input
                type="file"
                onChange={handleDesignDocFileChange}
                className="w-full p-2 border rounded mb-4"
                accept=".pdf,.doc,.docx"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAddDesignDoc(false);
                    setDesignDocFile(null);
                    setDesignDocError(null);
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitDesignDoc}
                  className="bg-blue-900 text-white px-4 py-2 rounded hover:bg-blue-800"
                  disabled={!designDocFile || submittingDesignDoc}
                >
                  {submittingDesignDoc ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Buildings Section */}
        <div className="bg-white rounded-3xl border p-6 mx-6">
          <h2 className="text-blue-900 text-xl font-bold mb-4">Buildings</h2>
          {loadingBuildings ? (
            <p>Loading buildings...</p>
          ) : buildings.length > 0 ? (
            <ul className="space-y-3">
              {buildings
              .sort((a, b) => a.id - b.id)
              .map((building) => (
                <li
                  key={building.id}
                  className="p-4 border bg-sky-200 font-semibold rounded-lg flex hover:bg-blue-200 justify-between items-center"
                >
                  {building.building_name}
                  <button
                    type="button"
                    className="px-4 py-2 text-blue-900 rounded hover:bg-gray-100"
                    onClick={() => handleBuildingView(building.id)}
                  >
                    <ChevronRight/>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No buildings available.</p>
          )}
        </div>
      </div>

      {/* Modal for Adding Build Process */}
      {showAddProcessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Build Process</h2>
            {addProcessError && <p className="text-red-500 mb-2">{addProcessError}</p>}
            <form onSubmit={handleAddBuildProcess}>
              <div className="mb-4">
                <label className="block text-gray-700">Title</label>
                <input
                  type="text"
                  value={buildProcessName}
                  onChange={(e) => setBuildProcessName(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Description</label>
                <textarea
                  value={buildProcessDescription}
                  onChange={(e) => setBuildProcessDescription(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                  rows={3}
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">File (PDF)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setBuildProcessFile(e.target.files[0]);
                    }
                  }}
                  className="w-full"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddProcessModal(false)}
                  className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                  disabled={addingProcess}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-blue-800 text-white hover:bg-blue-900"
                  disabled={addingProcess}
                >
                  {addingProcess ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Adding Work Instruction */}
      {showAddWorkInstructionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Work Instruction</h2>
            {addWorkInstructionError && (
              <p className="text-red-500 mb-2">{addWorkInstructionError}</p>
            )}
            <form onSubmit={handleAddWorkInstruction}>
              <p className="mb-4 text-sm text-gray-600">
                Upload at least one: work instruction, drawing, or video.
              </p>
              <div className="mb-4">
                <label className="block text-gray-700">Select Building</label>
                <select
                  value={wiBuildingId}
                  onChange={(e) => {
                    setWiBuildingId(e.target.value);
                    fetchModalWorkStations(e.target.value);
                    setWiWorkStationId('');
                  }}
                  className="w-full p-2 border bg-white rounded"
                  required
                >
                  <option value="">Select Building</option>
                  {buildings.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.building_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Select Work Station</label>
                <select
                  value={wiWorkStationId}
                  onChange={(e) => setWiWorkStationId(e.target.value)}
                  className="w-full p-2 border bg-white rounded"
                  required
                >
                  <option value="">Select Work Station</option>
                  {modalWorkStations.map((ws) => (
                    <option key={ws.id} value={ws.id}>
                      {ws.work_station}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Title</label>
                <input
                  type="text"
                  value={wiTitle}
                  onChange={(e) => setWiTitle(e.target.value)}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Description</label>
                <textarea
                  value={wiDescription}
                  onChange={(e) => setWiDescription(e.target.value)}
                  className="w-full p-2 border rounded"
                  rows={3}
                  required
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Work Instruction File (PDF, optional)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setWiFile(e.target.files[0]);
                    }
                  }}
                  className="w-full"
                />
              </div>
              <div className="mb-4">
                <p className="font-semibold text-blue-900">Add Drawing (PDF or Image)</p>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setWiDrawingFile(e.target.files[0]);
                    }
                  }}
                  className="w-full"
                />
              </div>
              <div className="mb-4">
                <p className="font-semibold text-blue-900">Add Video (optional)</p>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setWiVideoFile(e.target.files[0]);
                    }
                  }}
                  className="w-full"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddWorkInstructionModal(false)}
                  className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                  disabled={addingWorkInstruction}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-blue-800 text-white hover:bg-blue-900"
                  disabled={addingWorkInstruction}
                >
                  {addingWorkInstruction ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNav showBack={true} />
    </div>
    </MainLayout>
  );
};

export default DynamicWorkstationPage;
